import React from 'react';
import styled from 'styled-components';
import { Body7, Container, Select, TextField } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { FilterChip } from './FilterChip';
import { IndexFilter } from './IndexFilter';
import { KnowableWindowFilter } from './KnowableWindowFilter';
import { filterBarModel } from './filterBarModel';
import { isFiltering } from './rowFilters';

const Bar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

// @ui's TextField and Select own the border, the radius, the font and the focus ring. What they
// cannot own is how a control sits in THIS bar: both wrap themselves in a full-width column, which
// is right for a form and wrong for a row of controls that has to stay one line tall.
//
// So the sizing lives here, on the caller, where it belongs — and it is the sizing the bar already
// had rather than a new opinion. 34px is the bar's control height, shared with the knowable-window
// trigger beside it; 220px is what the filter input needs before a company name starts truncating.
// Both were load-bearing and neither is expressible as a prop today.
const BarField = styled.div`
  flex: 0 0 auto;
  min-width: ${({ $minWidth }) => $minWidth || 'auto'};

  input,
  select {
    height: 34px;
    padding: 0 10px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  input::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

// Narrowing what's already loaded, without another round trip. Browser find-in-page can't do this
// job: rows past the current page aren't in the DOM to be found.
export const TableControls = ({
  filters,
  onFilterChange,
  onClearFilters,
  missingCount,
  matchCount,
  totalCount,
  sectors = [],
  indexOptions = [],
  hasUnindexed = false,
  hasInputs = false,
  knowableSpan = null,
  aggregate = false,
  onAggregateChange = null,
  canAggregate = false,
}) => {
  // The gating policy, decided in one pure place. This component is layout from here down.
  const model = filterBarModel({
    missingCount,
    matchCount,
    totalCount,
    sectors,
    indexOptions,
    knowableSpan,
    hasInputs,
    canAggregate,
    canChangeAggregate: !!onAggregateChange,
    isFiltering: isFiltering(filters),
  });

  return (
    <Container bottom={16}>
      <Bar>
        {/* aria-label rather than TextField's visible `label`, deliberately: a visible label would
            change this control's accessible name, which is what every test in the feature queries
            it by — and the placeholder already says the same thing in half a bar's height. */}
        <BarField $minWidth="220px">
          <TextField
            value={filters.query}
            onChange={(query) => onFilterChange({ query })}
            placeholder="Filter loaded rows by symbol or name"
            aria-label="Filter loaded rows by symbol or name"
            spellCheck={false}
          />
        </BarField>
        {model.showMissing && (
          <FilterChip
            pressed={filters.missingOnly}
            onPress={() => onFilterChange({ missingOnly: !filters.missingOnly })}
          >
            {model.missingLabel}
          </FilterChip>
        )}
        {model.showSector && (
          <BarField>
            <Select
              aria-label="Filter by sector"
              value={filters.sector}
              onChange={(sector) => onFilterChange({ sector })}
              placeholder="All sectors"
              options={sectors.map((sector) => ({ value: sector, label: sector }))}
            />
          </BarField>
        )}
        {/* Offered only when some company in the filtered rows actually holds more than one
            occasion. With one row each it would redraw the table it sits above, and the analyst
            would have pressed it to learn nothing — the same rule as the selects above.

            A pressed toggle rather than a checkbox: this bar has no checkboxes, and SkeletonButton
            already paints aria-pressed. The label says what the press DID, not what it is, because
            the table beneath it changes grain when it goes on. */}
        {model.showAggregate && (
          <FilterChip
            pressed={aggregate}
            title="Average each company’s scores across the occasions shown, into a single row"
            onPress={() => onAggregateChange(!aggregate)}
          >
            Average per company
          </FilterChip>
        )}
        {/* Last in the bar, and not by accident: its open panel is a full-width flex item, so it
            wraps onto a line of its own — and anything after it here would be pushed onto a third.
            Offered only when the loaded rows straddle more than one UTC day; a single-occasion result
            has nothing for a date filter to narrow, and the chip could only reproduce the table it
            sits above. */}
        {/* No gate here: KnowableWindowFilter owns its own visibility rule, so the bar and the
            control cannot disagree about when it exists. The model still reports showWindow, which
            is what a redesign consolidating this bar reads to lay itself out. */}
        <KnowableWindowFilter filters={filters} onFilterChange={onFilterChange} span={knowableSpan} />
      </Bar>
      {/* Below the bar rather than in it: a set of indices is a row of chips, and a row of chips
          wraps. See IndexFilter for why it is not a dropdown. */}
      <IndexFilter
        options={indexOptions}
        selected={filters.memberships}
        hasUnindexed={hasUnindexed}
        onChange={(memberships) => onFilterChange({ memberships })}
      />
      {/* One clear for the whole bar, rather than one per control.
          //
          The only clear-all used to live inside IndexFilter, behind its own early return — so it
          was unreachable exactly when it was most needed: a filter whose control has unmounted
          still narrows nothing now, but a filter whose control is merely SCROLLED PAST, or behind a
          closed popover after a consolidation redesign, is still in force and had nowhere to be
          undone. Gated on something actually being filtered, which is the bar's own rule: a control
          that cannot change what is on screen is worse than no control. */}
      {model.showClearAll && (
        <Container top={8}>
          <SkeletonButton onClick={onClearFilters}>Clear all filters</SkeletonButton>
        </Container>
      )}
      {model.showMatchCount && (
        <Container top={4}>
          <Body7 isLowContrast role="status">
            {model.matchLabel}
          </Body7>
        </Container>
      )}
    </Container>
  );
};
